/* FSS reusable collection components (V4, 2026-09-27; V5 radio-group finder, hidden form context; V6: sortable comparison with
   per-row rate choice, shared commodity photo strip, photo goal cards, in-view muted video, guides released after a
   Shopify Forms submission).
   - Catalog: form filter (counts rendered by Liquid; hides cards only on the current, already-grouped page) and sort
     (native sort_by parameter; the server re-renders in Shopify's sorted order).
   - Comparison (V6): every product row stays; one commodity at a time from the shared photo strip. Each row gets its
     valid rate choices from the estimator plugin (Low/Medium/High, Low/High or Single; a new commodity resets every row
     to its default), one cost at the chosen rate, and all seven headings sort both ways (aria-sort; rows without a
     value stay last; rates sort within unit groups). Below 990px a Sort by select + direction button replace headings.
   - "Help me choose": the same commodity strip plus goal photo cards, usable in either order; direct-treatment goals
     ask for the missing commodity, facility goals need none; verified routes, leading matches first, no scrolling.
     Choosing a commodity in either strip updates both without moving focus or clearing the goal.
   - Help form(s): every help link uses one handler: context goes to hidden fields plus a readable summary; the
     Interest box is prefilled only while empty or still holding the page's own text (typed text is kept); the form
     heading scrolls into view below any sticky header and takes focus; reduced motion honoured; native anchors remain
     the no-JavaScript fallback. Accessible validation.
     V7F: a help link may name its own request label (data-fss-request-label) and message starter
     (data-fss-interest-text). An email/call panel (data-fss-help-form="direct") takes the same context and writes it
     into its mailto: links; opening a draft sends nothing and nothing is ever submitted by this script.
   - Video: click-to-load, or (data-fss-video-auto) the YouTube IFrame Player API playing MUTED from the start time
     when the player is in view; pauses out of view or in a hidden tab; resumes, never restarts or unmutes on its own,
     respects the visitor's pause; fallbacks for reduced motion, privacy preference, blocked autoplay and API failure.
   - Guides: Shopify Forms (app blocks in dialogs) save the lead; ?guide=<key> after the form's redirect releases the PDF.
   - Custom events via Shopify.analytics.publish (no personal data).
   Several sections include this file; the guard below makes later copies no-ops. Content and routing rules come from
   section settings and block settings; nothing here knows about grain. */
(function () {
  'use strict';
  if (window.__fssComponents) return;
  window.__fssComponents = true;

  function track(name, params) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish('fss_' + name, params || {});
    } catch (e) { /* never break the page for analytics */ }
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  function money(cents) { return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function uniq(a) { var s = {}; return a.filter(function (x) { if (!x || s[x]) return false; s[x] = 1; return true; }); }
  function fill(t, map) { return String(t || '').replace(/\{(\w+)\}/g, function (m, k) { return map[k] != null ? map[k] : m; }); }
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function reduced() { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }
  function scrollTop(top, smooth) {
    try { window.scrollTo({ top: top, behavior: smooth && !reduced() ? 'smooth' : 'instant' }); } catch (e) { window.scrollTo(0, top); }
  }
  function onReady(fn) { var done = false; function run() { if (!done) { done = true; fn(); } } if (document.readyState === 'complete') run(); else { document.addEventListener('DOMContentLoaded', run); window.addEventListener('load', run); } }

  /* ---------- scrolling: one helper for every in-page jump ---------- */
  function headerOffset() {
    var off = 0;
    each(document.querySelectorAll('.section-header, .shopify-section-header-sticky, sticky-header, .header-wrapper'), function (el) {
      var cs = getComputedStyle(el), r = el.getBoundingClientRect();
      if ((cs.position === 'sticky' || cs.position === 'fixed') && r.height > 0 && r.bottom > 0) off = Math.max(off, r.bottom);
    });
    return off;
  }
  function scrollToEl(el) {
    function target() { return Math.max(0, Math.min(el.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 16, document.documentElement.scrollHeight - window.innerHeight)); }
    var y0 = window.pageYOffset, t0 = target(), vh = window.innerHeight;
    if (Math.abs(t0 - y0) > vh * 2) scrollTop(t0 - (t0 > y0 ? vh : -vh), false); // long jump: land one screen short, then glide
    scrollTop(target(), true);
    // Content above can still move while the page settles (fonts, lazy images, estimates filling in). Once the scroll
    // stops, re-aim at the target, unless the shopper has started scrolling themselves.
    var last = -1, still = 0, tries = 0, userMoved = false, EVTS = ['wheel', 'touchstart', 'keydown'];
    function stop() { userMoved = true; }
    EVTS.forEach(function (t) { window.addEventListener(t, stop, { once: true, passive: true }); });
    (function check() {
      if (userMoved) return;
      var y = window.pageYOffset;
      still = Math.abs(y - last) < 1 ? still + 1 : 0; last = y;
      if (still >= 3 || ++tries > 60) {
        EVTS.forEach(function (t) { window.removeEventListener(t, stop); });
        if (Math.abs(target() - y) > 4) scrollTop(target(), false);
        return;
      }
      setTimeout(check, 50);
    })();
  }
  function focusQuietly(el) { if (!el) return; if (!el.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(el.tagName)) el.setAttribute('tabindex', '-1'); try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }

  /* ---------- delegated tracking ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('a, button') : null;
    if (!t || !t.closest('.fss-c, #product-grid')) return;
    var named = t.closest('[data-fss-track]');
    if (named) track(named.getAttribute('data-fss-track'), { product: named.getAttribute('data-fss-product') || '' });
    var href = t.tagName === 'A' ? (t.getAttribute('href') || '') : '';
    var m = href.match(/\/products\/([^?#/]+)/);
    if (m) {
      var list = t.closest('[data-results]') ? 'finder' : t.closest('[data-compare]') ? 'compare' : t.closest('[data-catalog]') ? 'catalog' : t.closest('#product-grid') ? 'grid' : 'collection_content';
      track('product_click', { handle: m[1], list: list });
    }
  }, true);

  /* ---------- help form: prefill ---------- */
  var REQ = { product: 'Product help', bulk: 'Bulk order', application: 'Application help', guide: 'Guide request' };
  var CTX = ['request', 'product', 'package', 'commodity', 'goal'];
  function helpFormIn(el) { return el ? (el.hasAttribute('data-fss-help-form') ? el : el.querySelector('[data-fss-help-form]')) : null; }
  function formFor(a) {
    var href = a.getAttribute('href') || '';
    var tgt = (href.charAt(0) === '#' && href.length > 1) ? document.getElementById(href.slice(1)) : null;
    return helpFormIn(tgt) || document.querySelector('[data-fss-help-form]');
  }
  function finderState() {
    var fr = document.querySelector('[data-fss-finder][data-fss-cur1]');
    return fr ? { c1: fr.getAttribute('data-fss-cur1') || '', c2: fr.getAttribute('data-fss-cur2') || '' } : { c1: '', c2: '' };
  }
  // Hidden context fields are submitted only when they hold a value (an empty field is disabled).
  function setHidden(form, key, val) { var el = form.querySelector('[data-fss-hidden="' + key + '"]'); if (!el) return; el.value = val || ''; el.disabled = !val; }
  function getHidden(form, key) { var el = form.querySelector('[data-fss-hidden="' + key + '"]'); return el && !el.disabled ? el.value : ''; }
  function updateSummary(form) {
    var box = form.querySelector('[data-fss-context-summary]');
    if (!box) return;
    // Shown whenever a link has given the form any context (the request type alone included).
    var parts = CTX.map(function (k) { return getHidden(form, k); }).filter(Boolean);
    var label = box.getAttribute('data-label') || 'Your request';
    box.innerHTML = parts.length ? '<strong>' + esc(label) + ':</strong> ' + parts.map(esc).join(' <span aria-hidden="true">·</span> ') : '';
    box.hidden = !parts.length;
  }
  function interestText(form) {
    var req = getHidden(form, 'request'), rest = CTX.slice(1).map(function (k) { return getHidden(form, k); }).filter(Boolean);
    return req + (rest.length ? ': ' + rest.join(', ') : '');
  }
  function prefillHelp(a, form) {
    var req = a.getAttribute('data-fss-request') || 'product';
    // Only links inside the finder take context they do not name (commodity and/or goal) from the shopper's current
    // finder choices; every other help link sends just the context it names (no unverified product/use pairings).
    var c1 = a.getAttribute('data-fss-commodity-text'), c2 = a.getAttribute('data-fss-goal-text');
    var st = a.closest('[data-finder-disclosure]') ? finderState() : { c1: '', c2: '' };
    if (c1 == null) c1 = st.c1;
    if (c2 == null) c2 = st.c2;
    setHidden(form, 'request', a.getAttribute('data-fss-request-label') || REQ[req] || req);
    setHidden(form, 'product', a.getAttribute('data-fss-product-title') || '');
    setHidden(form, 'package', a.getAttribute('data-fss-package') || '');
    setHidden(form, 'commodity', c1 || '');
    setHidden(form, 'goal', c2 || '');
    var box = form.querySelector('[data-fss-interest]');
    if (box && (!box.value.trim() || box.getAttribute('data-fss-auto') === '1')) { box.value = a.getAttribute('data-fss-interest-text') || interestText(form); box.setAttribute('data-fss-auto', '1'); }
    updateSummary(form);
    refreshMail(form);
  }
  /* V7F email/call panel: subject = request (+ product); body = intro, context lines, the visitor's message (unless it is
     still the page's own copy of the context), closing lines and the page address without its query string. */
  var MAIL_LBL = { product: 'Product', package: 'Package', commodity: 'Commodity', goal: 'Goal' };
  // cut a note to n UTF-16 units without leaving half of a surrogate pair (encodeURIComponent throws on a lone half)
  function cutNote(s, n) { return s.slice(0, n).replace(/[\uD800-\uDBFF]$/, ''); }
  function refreshMail(panel) {
    var to = panel.getAttribute('data-fss-mail-to');
    if (!to || panel.getAttribute('data-fss-help-form') !== 'direct') return;
    var req = getHidden(panel, 'request'), prod = getHidden(panel, 'product');
    var subject = req ? req + (prod ? ': ' + prod : '') : (panel.getAttribute('data-fss-mail-subject') || '');
    var intro = panel.getAttribute('data-fss-mail-intro') || '', tail = panel.getAttribute('data-fss-mail-tail') || '';
    var ctx = Object.keys(MAIL_LBL).map(function (k) { var v = getHidden(panel, k); return v ? MAIL_LBL[k] + ': ' + v : ''; }).filter(Boolean);
    var box = panel.querySelector('[data-fss-interest]'), note = box ? cutNote(box.value.trim(), 1500) : '';
    if (note && box.getAttribute('data-fss-auto') === '1' && note === interestText(panel)) note = '';
    function build(n) {
      var lines = [];
      if (intro) lines.push(intro, '');
      if (ctx.length) lines = lines.concat(ctx);
      if (n) lines.push(ctx.length ? '' : null, n);
      if (tail) lines.push('', tail.replace(/\r\n?/g, '\n'));
      lines.push('', 'Page: ' + window.location.origin + window.location.pathname);
      var body = lines.filter(function (x) { return x !== null; }).join('\n').replace(/\n/g, '\r\n');
      return 'mailto:' + to + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
    }
    // V7F fix: some mail apps truncate or refuse very long mailto links, so keep the whole link under ~1,900 characters
    // by shortening the visitor's note (never the context) and say so under the message box.
    function safeBuild(n) { try { return build(n); } catch (e) { return build(''); } }
    var href = safeBuild(note), cut = note.length, trimmed = false;
    while (href.length > 1900 && cut > 0) { cut = cut > 60 ? Math.floor(cut * 0.8) : 0; trimmed = true; href = safeBuild(cut ? cutNote(note, cut) + ' [...]' : ''); }
    var trimHint = panel.querySelector('[data-fss-mail-trim]'); if (trimHint) trimHint.hidden = !trimmed;
    each(panel.querySelectorAll('a[data-fss-mailto]'), function (el) { el.setAttribute('href', href); });
  }
  function initDirect(panel) {
    var nf = panel.querySelector('[data-fss-note-field]');
    if (nf) nf.hidden = false; // the message box only helps when this script can copy it into the draft
    var nbox = panel.querySelector('[data-fss-interest]');
    if (nbox && !panel.querySelector('[data-fss-mail-trim]')) { var th = document.createElement('p'); th.className = 'fss-field__hint'; th.setAttribute('data-fss-mail-trim', ''); th.setAttribute('role', 'status'); th.hidden = true; th.textContent = 'Long message shortened in the email draft. Add the rest in your email.'; nbox.insertAdjacentElement('afterend', th); }
    function edited(e) { if (e.target && e.target.removeAttribute) e.target.removeAttribute('data-fss-auto'); updateSummary(panel); refreshMail(panel); }
    panel.addEventListener('input', edited);
    panel.addEventListener('change', edited);
    // rebuild just before the email app opens; the link itself opens the draft (no preventDefault, nothing sent)
    panel.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('a[data-fss-mailto]')) refreshMail(panel); }, true);
    updateSummary(panel);
    refreshMail(panel);
  }
  function goToHelp(form) {
    var head = (form.closest('[data-fss-help-target]') || form).querySelector('[data-fss-help-heading]');
    scrollToEl(head || form);
    focusQuietly(head || form);
  }

  function setHash(href) {
    if (!href || href.charAt(0) !== '#' || href.length < 2 || window.location.hash === href) return;
    try { history.pushState(history.state, '', href); } catch (e) { /* keep the page working */ }
  }

  /* ---------- one handler for in-page links: help form, disclosures, component anchors ---------- */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest ? e.target.closest('a[href*="#"], a[data-fss-request]') : null;
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (href.charAt(0) !== '#') {
      // a full or root-relative URL to THIS page with a fragment behaves like an in-page link
      try { var u = new URL(a.href, window.location.href); href = (u.origin === window.location.origin && u.pathname === window.location.pathname && u.search === window.location.search && u.hash.length > 1) ? u.hash : ''; } catch (err) { href = ''; }
      if (!href && !a.hasAttribute('data-fss-request')) return;
    }
    var tgt = (href.charAt(0) === '#' && href.length > 1) ? document.getElementById(href.slice(1)) : null;
    var inC = !!(tgt && tgt.closest('.fss-c'));
    var form = a.hasAttribute('data-fss-request') ? formFor(a) : (inC ? helpFormIn(tgt) : null);
    if (form) {
      e.preventDefault();
      if (a.hasAttribute('data-fss-request')) prefillHelp(a, form);
      goToHelp(form);
      setHash(href);
      return;
    }
    if (!inC) return; // leave theme anchors (skip links etc.) alone
    e.preventDefault();
    setHash(href);
    if (tgt.tagName === 'DETAILS') { tgt.open = true; scrollToEl(tgt); focusQuietly(tgt.querySelector('summary')); return; }
    scrollToEl(tgt);
    focusQuietly(tgt.querySelector('h2, h3') || tgt);
  });

  /* ---------- help form validation ---------- */
  function dropPostedFlag() {
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.has('contact_posted')) { u.searchParams.delete('contact_posted'); history.replaceState(history.state, '', u.pathname + u.search + u.hash); }
    } catch (e) { /* keep the page working */ }
  }
  each(document.querySelectorAll('[data-fss-help-form]'), function (hf) {
    if (hf.getAttribute('data-fss-help-form') === 'direct') { initDirect(hf); return; }
    var ok = hf.querySelector('[data-fss-form-success]'), bad = hf.querySelector('[data-fss-form-errors]');
    if (ok) { focusQuietly(ok); ok.scrollIntoView({ block: 'center', behavior: 'instant' }); track('help_form_sent', {}); dropPostedFlag(); } else if (bad) { focusQuietly(bad); bad.scrollIntoView({ block: 'center', behavior: 'instant' }); }
    hf.setAttribute('novalidate', '');
    var summary = hf.querySelector('[data-fss-form-summary]');
    var FIELDS = [
      { el: hf.querySelector('[data-fss-field="name"]'), err: hf.querySelector('[data-fss-err="name"]'), check: function (v) { return v.trim() ? '' : 'Enter your name.'; } },
      { el: hf.querySelector('[data-fss-field="email"]'), err: hf.querySelector('[data-fss-err="email"]'), check: function (v) { return !v.trim() ? 'Enter your email address.' : (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) ? '' : 'Enter an email address like name@example.com.'); } }
    ];
    var setErr = function (f, msg) { if (!f.el || !f.err) return; f.err.textContent = msg; f.err.hidden = !msg; if (msg) f.el.setAttribute('aria-invalid', 'true'); else f.el.removeAttribute('aria-invalid'); };
    FIELDS.forEach(function (f) { if (f.el) f.el.addEventListener('input', function () { if (f.el.getAttribute('aria-invalid')) setErr(f, f.check(f.el.value)); }); });
    function userEdit(e) { if (e.target && e.target.removeAttribute) e.target.removeAttribute('data-fss-auto'); updateSummary(hf); }
    hf.addEventListener('input', userEdit);
    hf.addEventListener('change', userEdit);
    hf.addEventListener('submit', function (e) {
      var errs = [];
      FIELDS.forEach(function (f) { var m = f.el ? f.check(f.el.value) : ''; setErr(f, m); if (m) errs.push({ id: f.el.id, msg: m }); });
      if (errs.length) {
        e.preventDefault();
        summary.innerHTML = '<p><strong>Please fix ' + (errs.length === 1 ? 'this' : 'these') + ' before sending:</strong></p><ul>' + errs.map(function (x) { return '<li><a href="#' + esc(x.id) + '">' + esc(x.msg) + '</a></li>'; }).join('') + '</ul>';
        summary.hidden = false; scrollToEl(summary); focusQuietly(summary); return;
      }
      summary.hidden = true;
      track('help_form_submit', { request_type: getHidden(hf, 'request') });
    });
    if (summary) summary.addEventListener('click', function (e) { var a = e.target.closest('a[href^="#"]'); if (!a) return; var t = document.getElementById(a.getAttribute('href').slice(1)); if (t) { e.preventDefault(); e.stopPropagation(); scrollToEl(t.closest('.fss-field') || t); t.focus({ preventScroll: true }); } });
    updateSummary(hf);
  });

  /* ---------- YouTube: click-to-load (default) or muted play-in-view (data-fss-video-auto) ----------
     In-view design (ported from the tested V6 discovery prototype, build/discovery/video/proto; 61/61 checks against the
     real player under Chrome's default autoplay policy). Documented APIs only:
     - Privacy Enhanced Mode = our own <iframe src="https://www.youtube-nocookie.com/embed/ID?enablejsapi=1&origin=...">
       then new YT.Player(iframe, {events}) (IFrame API reference, Example 1); no undocumented host option or mute
       parameter; mute() before playVideo() for the first automatic playback; unMute() is never called.
     - Automatic playback only when the player is fully visible (>= 99 %, or the largest share that fits), never below
       "more than half", and only when nothing (chat launcher, preview bar) sits in front of it; pauses below 50 % or when
       the tab is hidden; resumes from where it paused; a pause we did not request is the visitor's and is respected.
     - Consent: Shopify Customer Privacy API via Shopify.loadFeatures; automatic loading needs marketing + analytics +
       sale-of-data allowed; visitorConsentCollected re-evaluates. Declined: the poster and links open YouTube instead.
     - Reduced motion, a blocked autoplay, an API or player error: the poster link and the "Play from m:ss" button (or
       the timestamped YouTube link) keep working; the poster is removed only when the player is ready. */
  var YT_API = 'https://www.youtube.com/iframe_api', YT_EMBED = 'https://www.youtube-nocookie.com/embed/';
  var YT_ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
  var ytApi = null, ytActive = null;
  function loadYouTubeApi() {
    if (ytApi) return ytApi;
    ytApi = new Promise(function (resolve, reject) {
      if (window.YT && window.YT.Player) { resolve(window.YT); return; }
      var prev = window.onYouTubeIframeAPIReady; // no fixed timeout: the player's visitor-wait clock decides (waitClock)
      window.onYouTubeIframeAPIReady = function () { if (typeof prev === 'function') { try { prev(); } catch (e) { /* other embeds */ } } resolve(window.YT); };
      var s = document.createElement('script');
      s.src = YT_API; s.async = true;
      s.onerror = function () { reject(new Error('api')); };
      document.head.appendChild(s);
    });
    return ytApi;
  }
  // Store consent: 'allowed' | 'denied' | 'unknown'. Regions without a consent requirement return true from the Allowed
  // methods; banner regions return false until the visitor opts in.
  function consentAllowed(cp) { try { return !!(cp.marketingAllowed() && cp.analyticsProcessingAllowed() && (typeof cp.saleOfDataAllowed !== 'function' || cp.saleOfDataAllowed())); } catch (e) { return false; } }
  function readConsent(cb) {
    var S = window.Shopify, done = false;
    // a definite answer that arrives after the 5 s cutoff still counts (the cutoff only unblocks the page)
    function finish(v, late) { if (!done) { done = true; cb(v); } else if (late && v !== 'unknown') cb(v); }
    if (!S || typeof S.loadFeatures !== 'function') { finish('allowed'); return; }
    setTimeout(function () { finish('unknown'); }, 5000);
    try {
      S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
        if (err || !window.Shopify.customerPrivacy) { finish('unknown'); return; }
        finish(consentAllowed(window.Shopify.customerPrivacy) ? 'allowed' : 'denied', true);
      });
    } catch (e) { finish('unknown'); }
  }
  function videoInView(box) {
    var id = box.getAttribute('data-fss-video');
    if (!/^[\w-]{11}$/.test(id)) return;
    var media = box.parentNode, poster = box.querySelector('a');
    var startBtn = media.querySelector('[data-fss-video-startbtn]'), status = media.querySelector('[data-fss-video-status]');
    var note = box.querySelector('[data-fss-play-note]');
    var start = Math.max(0, parseInt(box.getAttribute('data-fss-video-start'), 10) || 0);
    var title = box.getAttribute('data-fss-video-title') || 'Video';
    var rmq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var consent = 'pending', autoOk = false;
    var player = null, iframe = null, ready = false, building = false, failed = false;
    var played = false, firstAuto = false, userPaused = false, autoPaused = false, ended = false, blocked = false;
    var ourPauseAt = 0, userPausedAt = 0, hidPlaying = false, clearTries = 0, wantUser = null, watchdog = 0, clearTimer = 0, readyTimer = 0, lastSaid = '', near = false;
    var ctl = { pause: function () { var s = st(); if (player && ready && (s === 1 || s === 3)) { ourPauseAt = 0; player.pauseVideo(); } } };
    function setState(s) { box.setAttribute('data-fss-video-state', s); }
    function say(m) { if (!status || m === lastSaid) return; lastSaid = m; status.textContent = ''; setTimeout(function () { status.textContent = m; }, 60); }
    function st() { try { return player.getPlayerState(); } catch (e) { return -1; } }
    function ratio() {
      var r = box.getBoundingClientRect(), de = document.documentElement, vw = de.clientWidth || window.innerWidth, vh = de.clientHeight || window.innerHeight;
      var w = Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0)), h = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
      return r.width * r.height ? (w * h) / (r.width * r.height) : 0;
    }
    function need() { var r = box.getBoundingClientRect(), vh = document.documentElement.clientHeight || window.innerHeight; return r.height <= vh ? 0.99 : Math.max(0.51, Math.min(0.99, 0.95 * vh / r.height)); }
    // Nothing may sit in front of the player: every visible sample point must hit the frame (or an ancestor behind it).
    function unobscured() {
      var r = box.getBoundingClientRect(), de = document.documentElement, vw = de.clientWidth || window.innerWidth, vh = de.clientHeight || window.innerHeight;
      var nx = Math.min(16, Math.max(3, Math.ceil(r.width / 40) + 1)), ny = Math.min(10, Math.max(3, Math.ceil(r.height / 40) + 1));
      for (var i = 0; i < nx; i++) for (var j = 0; j < ny; j++) {
        var x = r.left + 2 + (r.width - 4) * i / (nx - 1), y = r.top + 2 + (r.height - 4) * j / (ny - 1);
        if (x < 0 || y < 0 || x >= vw || y >= vh) continue;
        var el = document.elementFromPoint(x, y);
        if (el && !box.contains(el) && !el.contains(box)) return false;
      }
      return true;
    }
    function clearWatch() { if (watchdog) { clearTimeout(watchdog); watchdog = 0; } }
    // The 15 s load budget runs only while the visitor is actually waiting (player half visible, or they pressed play).
    function waitClock() {
      if ((!player && !building) || ready || failed) return;
      var waiting = !!wantUser || (!document.hidden && ratio() >= 0.5);
      if (waiting && !readyTimer) readyTimer = setTimeout(function () { readyTimer = 0; if (!ready && !failed) fallback('timeout'); }, 15000);
      else if (!waiting && readyTimer) { clearTimeout(readyTimer); readyTimer = 0; }
    }
    function ourPause() { var s = st(); clearWatch(); if (player && ready && (s === 1 || s === 3)) { ourPauseAt = Date.now(); player.pauseVideo(); } }
    function autoPlay() {
      if (!played) { player.mute(); firstAuto = true; }
      clearTries = 0; autoPaused = false; player.playVideo(); clearWatch();
      watchdog = setTimeout(function () { watchdog = 0; var s = st(); if ((s === -1 || s === 5) && !blocked) onBlocked('watchdog'); }, 4000);
    }
    function userPlay(fromStart) {
      if (fromStart) player.seekTo(start, true);
      blocked = false; userPaused = false; autoPaused = false; ended = false;
      player.playVideo(); // mute state untouched
    }
    var wakeArmed = false;
    function wake() { wakeArmed = false; window.removeEventListener('scroll', wake); document.removeEventListener('pointerup', wake); document.removeEventListener('keyup', wake); clearTries = 0; clearTimeout(clearTimer); clearTimer = setTimeout(evaluate, 300); }
    function wakeOnInput() { if (wakeArmed) return; wakeArmed = true; window.addEventListener('scroll', wake, { passive: true, once: true }); document.addEventListener('pointerup', wake, { passive: true, once: true }); document.addEventListener('keyup', wake, { once: true }); }
    function evaluate() {
      clearTimeout(clearTimer); clearTimer = 0;
      if (failed) return;
      if (!player || !ready) { waitClock(); if (autoOk && near) build(false); return; }
      var r = ratio(), s = st();
      if (document.hidden || r < 0.5) { if (s === 1 || s === 3) ourPause(); return; }
      if (!autoOk || blocked || userPaused || ended || reduced()) return;
      if (s === 1 || s === 3) return;
      if (played && !autoPaused) return;
      if (r >= need()) {
        if (unobscured()) autoPlay();
        else { setState(played ? 'paused-auto' : 'waiting-clear'); if (++clearTries <= 10) clearTimer = setTimeout(evaluate, 300); else wakeOnInput(); } // about 3 s, then re-arm on the next scroll, tap/click or key
      }
    }
    function posterKbd(on) { if (!poster) return; if (on) { poster.removeAttribute('tabindex'); poster.removeAttribute('aria-hidden'); } else { poster.setAttribute('tabindex', '-1'); poster.setAttribute('aria-hidden', 'true'); } }
    function restorePoster() { if (poster && !poster.parentNode) box.appendChild(poster); if (note) note.textContent = 'opens YouTube in a new tab'; }
    function fallback(reason, msg) {
      failed = true; building = false; ready = false; clearWatch(); clearTimeout(readyTimer); readyTimer = 0;
      try { if (player && player.destroy) player.destroy(); } catch (e) { /* ignore */ }
      if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
      player = null; iframe = null;
      restorePoster();
      var hadFocus = startBtn && document.activeElement === startBtn;
      if (startBtn) startBtn.hidden = true;
      posterKbd(true);
      if (hadFocus) focusQuietly(media.querySelector('a[data-fss-video-yt], a[data-fss-track="video_youtube"]') || poster);
      setState('fallback-' + reason);
      say(msg || 'The video player could not load here. Watch on YouTube instead.');
      track('video_fallback', { id: id, reason: reason });
    }
    function onBlocked(src) {
      if (blocked) return;
      blocked = true; clearWatch(); setState('blocked'); box.setAttribute('data-fss-video-reason', src);
      if (startBtn) startBtn.hidden = false;
      say('Autoplay is blocked in this browser. Use the play button to watch.');
      track('video_autoplay_blocked', { id: id, source: src });
    }
    function build(user) {
      if (user) wantUser = user;
      if (player || building || failed) { waitClock(); return; }
      building = true; setState('loading'); waitClock();
      loadYouTubeApi().then(function (YT) {
        if (failed) return;
        iframe = document.createElement('iframe');
        iframe.id = 'fss-yt-' + id + '-' + Math.random().toString(36).slice(2, 7);
        iframe.src = YT_EMBED + id + '?enablejsapi=1&origin=' + encodeURIComponent(location.origin) + (start ? '&start=' + start : '') + '&playsinline=1&rel=0&cc_load_policy=1';
        iframe.title = title;
        iframe.setAttribute('allow', YT_ALLOW);
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        // Transparent + inert until onReady (the poster covers it anyway). Not visibility:hidden: Chrome throttles a
        // cross-origin frame that is not rendered, which stalled the player for 10-35 s in review.
        iframe.style.opacity = '0'; iframe.setAttribute('inert', ''); iframe.setAttribute('tabindex', '-1'); iframe.setAttribute('aria-hidden', 'true');
        box.insertBefore(iframe, box.firstChild);
        player = new YT.Player(iframe, {
          events: {
            onReady: function () {
              clearTimeout(readyTimer); readyTimer = 0; ready = true; building = false;
              var hadFocus = poster && document.activeElement === poster;
              iframe.style.opacity = ''; iframe.removeAttribute('inert'); iframe.removeAttribute('tabindex'); iframe.removeAttribute('aria-hidden');
              say('');
              if (poster && poster.parentNode) poster.parentNode.removeChild(poster);
              setState('ready');
              if (wantUser) { var w = wantUser; wantUser = null; userPlay(w === 'restart'); if (w === 'poster' || hadFocus) iframe.focus(); return; }
              if (hadFocus) iframe.focus();
              evaluate(); // re-measures now: a slow load never starts off-screen
            },
            onStateChange: function (ev) {
              var s = ev.data;
              if (s === 1) {
                clearWatch(); blocked = false;
                if (ytActive && ytActive !== ctl) ytActive.pause(); // one playing player per page
                ytActive = ctl;
                if (firstAuto) { firstAuto = false; try { if (!player.isMuted()) player.mute(); } catch (e) { /* safety net */ } }
                played = true; userPaused = false; autoPaused = false; ended = false; setState('playing');
                if (!document.hidden) hidPlaying = false;
                if (document.hidden || ratio() < 0.5) ourPause();
              } else if (s === 2) {
                // A pause that follows hiding the tab is ours however late its message arrives (hidden pages get it late).
                if (hidPlaying || document.hidden || (ourPauseAt && Date.now() - ourPauseAt < 2500)) { ourPauseAt = 0; hidPlaying = false; autoPaused = true; setState('paused-auto'); if (!document.hidden) setTimeout(evaluate, 0); }
                else { userPaused = true; userPausedAt = Date.now(); autoPaused = false; setState('paused-user'); }
              } else if (s === 0) { ended = true; autoPaused = false; setState('ended'); }
            },
            onAutoplayBlocked: function () { onBlocked('api'); },
            onError: function (ev) { if (!played) fallback('error-' + ev.data); else { setState('error-' + ev.data); say('The video stopped with an error. Watch on YouTube instead.'); } }
          }
        });
        waitClock();
      }, function (err) { fallback(err && err.message === 'timeout' ? 'timeout' : 'api'); });
    }
    function wireClicks() {
      if (note) note.textContent = note.getAttribute('data-fss-play-note') || 'loads the YouTube player';
      if (startBtn) { startBtn.hidden = false; posterKbd(false); }
      if (poster) poster.addEventListener('click', function (e) {
        if (failed || consent === 'denied') return; // plain link: YouTube in a new tab
        e.preventDefault();
        if (player && ready) { userPlay(false); iframe.focus(); } else { say('Loading the video…'); build('poster'); }
        track('video_play', { id: id });
      });
      if (startBtn) startBtn.addEventListener('click', function () {
        if (failed) return;
        if (player && ready) userPlay(true); else { say('Loading the video…'); build('restart'); }
        track('video_start_button', { id: id });
      });
    }
    var wired = false;
    function wireOnce() { if (!wired) { wired = true; wireClicks(); } else { if (note) note.textContent = note.getAttribute('data-fss-play-note') || 'loads the YouTube player'; if (startBtn) { startBtn.hidden = false; posterKbd(false); } } }
    function applyConsent(c) {
      var was = consent; consent = c;
      autoOk = consent === 'allowed' && !reduced() && 'IntersectionObserver' in window;
      if (consent === 'denied') {
        if (player) { ourPause(); fallback('declined', 'Video playback here is off under your privacy choices. Watch on YouTube instead.'); failed = false; }
        if (note) note.textContent = 'opens YouTube in a new tab';
        var hadFocusD = startBtn && document.activeElement === startBtn;
        if (startBtn) startBtn.hidden = true;
        posterKbd(true);
        if (hadFocusD) focusQuietly(poster);
        setState('declined');
        return;
      }
      if (was === 'denied' || was === 'pending') wireOnce();
      if (!player) setState(consent === 'unknown' ? 'consent-unknown' : reduced() ? 'reduced-motion' : ('IntersectionObserver' in window ? 'idle' : 'no-observer'));
      evaluate();
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { es.forEach(function (en) { near = en.isIntersecting; if (near) evaluate(); }); }, { rootMargin: '300px 0px' }).observe(box);
      // dense thresholds from 50 %: need() can sit between coarse steps (a tall player on a short screen)
      var ths = [0, 0.25]; for (var t = 50; t <= 100; t++) ths.push(t / 100);
      new IntersectionObserver(function () { clearTries = 0; evaluate(); }, { threshold: ths }).observe(box);
    }
    var rsz = 0; window.addEventListener('resize', function () { clearTimeout(rsz); rsz = setTimeout(function () { clearTries = 0; evaluate(); }, 150); });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && player && ready) {
        var s = st();
        if (s === 1 || s === 3) hidPlaying = true;
      }
      evaluate();
    });
    if (rmq && rmq.addEventListener) rmq.addEventListener('change', function () { if (reduced()) ourPause(); applyConsent(consent); });
    document.addEventListener('visitorConsentCollected', function (ev) {
      var d = ev && ev.detail || {};
      applyConsent(d.marketingAllowed && d.analyticsAllowed && d.saleOfDataAllowed !== false ? 'allowed' : 'denied');
    });
    setState('consent-pending');
    readConsent(applyConsent);
  }
  each(document.querySelectorAll('[data-fss-video]'), function (box) {
    var id = box.getAttribute('data-fss-video');
    if (!/^[\w-]{6,20}$/.test(id)) return;
    if (box.hasAttribute('data-fss-video-auto')) { videoInView(box); return; }
    var a = box.querySelector('a');
    var start = parseInt(box.getAttribute('data-fss-video-start'), 10) || 0;
    var pn = box.querySelector('[data-fss-play-note]');
    // server text says the link opens YouTube (true without JavaScript); with the script it loads the player here
    if (!a) return;
    if (pn) pn.textContent = pn.getAttribute('data-fss-play-note');
    a.addEventListener('click', function (e) {
      e.preventDefault();
      var f = document.createElement('iframe');
      f.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&cc_load_policy=1&playsinline=1' + (start ? '&start=' + start : '');
      f.referrerPolicy = 'strict-origin-when-cross-origin';
      f.title = box.getAttribute('data-fss-video-title') || 'Video';
      f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      f.allowFullscreen = true;
      // data-fss-video-holder="" puts the iframe straight into an already-sized frame; otherwise a holder div is used.
      var hc = box.getAttribute('data-fss-video-holder'), node = f;
      if (hc !== '') { node = document.createElement('div'); node.className = hc || 'fss-doc__video'; node.appendChild(f); }
      a.parentNode.replaceChild(node, a);
      track('video_load', { id: id });
      f.focus();
    });
  });

  document.addEventListener('focusin', function (ev) {
    var t = ev.target;
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches || !t || !t.matches || !t.closest || !t.closest('.fss-c')) return;
    try { if (!t.matches(':focus-visible')) return; } catch (e) { return; }
    var r = t.getBoundingClientRect(), lim = window.innerHeight - 80;
    if (r.top >= 0 && r.bottom <= window.innerHeight && r.bottom > lim && r.height < lim) { try { window.scrollBy({ top: Math.ceil(r.bottom - lim), left: 0, behavior: 'instant' }); } catch (e) { window.scrollBy(0, Math.ceil(r.bottom - lim)); } }
  });

  /* ---------- guides: V7 uses ordinary server-rendered PDF links (no gate, no ?guide=, no storage) ---------- */

  /* ---------- catalog: form filter + sort ---------- */
  each(document.querySelectorAll('[data-catalog]'), function (cat) {
    var grid = cat.querySelector('[data-catalog-grid]'), count = cat.querySelector('[data-catalog-count]'), status = cat.querySelector('[data-catalog-status]');
    var facets = cat.querySelector('[data-catalog-facets]');
    if (facets && grid) {
      facets.hidden = false;
      facets.addEventListener('click', function (e) {
        var b = e.target.closest('[data-facet-btn]'); if (!b) return;
        var f = b.getAttribute('data-facet-btn'), shown = 0;
        each(facets.querySelectorAll('[data-facet-btn]'), function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        each(grid.children, function (li) { var on = f === 'all' || li.getAttribute('data-facet') === f; li.hidden = !on; if (on) shown++; });
        if (count) count.textContent = '(' + shown + ')';
        if (status) { status.textContent = ''; setTimeout(function () { status.textContent = shown + ' product' + (shown === 1 ? '' : 's') + ' shown.'; }, 60); }
        track('catalog_filter', { facet: f });
      });
    }
    var sortForm = cat.querySelector('[data-catalog-sort]');
    if (sortForm) {
      var sel = sortForm.querySelector('select'), viaPointer = false;
      var applySort = function () {
        var u = new URL(window.location.href);
        if (sel.value) u.searchParams.set('sort_by', sel.value); else u.searchParams.delete('sort_by');
        u.searchParams.delete('page');
        u.hash = cat.id || '';
        track('catalog_sort', { sort: sel.value || 'recommended' });
        window.location.href = u.toString();
      };
      sel.addEventListener('pointerdown', function () { viaPointer = true; });
      sel.addEventListener('keydown', function (e) { viaPointer = false; if (e.key === 'Enter') { e.preventDefault(); applySort(); } });
      sel.addEventListener('change', function () { if (viaPointer) applySort(); });
      sortForm.addEventListener('submit', function (e) { e.preventDefault(); applySort(); });
    }
  });

  /* ---------- comparison table + "Help me choose" (share one config and one commodity choice per section) ---------- */
  each(document.querySelectorAll('[data-fss-finder]'), function (root) {
    var cfgEl = root.querySelector('[data-fss-finder-config]');
    var cfg; try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    var T = cfg.texts || {}, P = cfg.products || {}, C1 = {}, C2 = {}, K1 = [];
    cfg.choice1.forEach(function (c) { C1[c.key] = c; K1.push(c.key); });
    cfg.choice2.forEach(function (c) { C2[c.key] = c; });
    var est = null;
    // Resolved on first use: sections earlier in the page also load this file, before the estimator (deferred).
    function E() { if (!est && cfg.dataset && window.FSSEstimators && window.FSSEstimators[cfg.dataset]) { est = window.FSSEstimators[cfg.dataset]; if (est.init) est.init(cfg); } return est; }
    function lc(s) { return String(s || '').toLowerCase(); }
    function appFor(p, ck) {
      var def = '', hit = '';
      String(p.apps || '').split(';').forEach(function (pr) { var i = pr.indexOf('='); if (i < 0) return; var k = pr.slice(0, i).trim(), v = pr.slice(i + 1).trim(); if (k === ck) hit = v; else if (k === '*') def = v; });
      return hit || def;
    }

    /* One commodity choice shared by both strips. The comparison always has one (default: cfg.defaultBasis); the finder
       starts empty so its goal can be chosen first. Choosing in either strip sets both, never scrolls, never moves
       focus and never clears the finder's goal. */
    var shared = { cmp: C1[cfg.defaultBasis] ? cfg.defaultBasis : K1[0], fc1: null };
    var strips = [].slice.call(root.querySelectorAll('[data-cstrip]'));
    function paintStrips() {
      strips.forEach(function (st) {
        var cur = st.getAttribute('data-cstrip') === 'compare' ? shared.cmp : shared.fc1;
        each(st.querySelectorAll('[data-commodity]'), function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-commodity') === cur ? 'true' : 'false'); });
      });
    }
    var hooks = { compare: null, finder: null };
    var lastPin = null;
    function chooseCommodity(key, from) {
      if (!C1[key]) return;
      var changedCmp = shared.cmp !== key, changedF = shared.fc1 !== key;
      // The comparison table sits above the finder strip and changes height with the commodity: keep the strip that was
      // used exactly where it was (measured synchronously, so browser scroll anchoring is never doubled).
      var pin = root.querySelector('[data-cstrip="' + from + '"]'), pinTop = pin ? pin.getBoundingClientRect().top : 0;
      if (lastPin && lastPin.el === pin && Math.abs(window.pageYOffset - lastPin.y) < 1) pinTop = lastPin.top;
      shared.cmp = key; shared.fc1 = key;
      paintStrips();
      if (hooks.compare && changedCmp) hooks.compare(key, from === 'compare');
      if (hooks.finder && (changedF || from === 'finder')) hooks.finder(from === 'finder');
      if (pin) { var drift = pin.getBoundingClientRect().top - pinTop; if (Math.abs(drift) >= 0.5) scrollTop(window.pageYOffset + drift, false); lastPin = { el: pin, top: pinTop, y: window.pageYOffset }; }
      track('commodity_select', { commodity: key, from: from });
    }
    strips.forEach(function (st) {
      st.addEventListener('click', function (ev) { var b = ev.target.closest('[data-commodity]'); if (b) chooseCommodity(b.getAttribute('data-commodity'), st.getAttribute('data-cstrip')); });
    });

    /* ---------------- comparison ---------------- */
    var compare = root.querySelector('[data-compare]');
    var tbody = compare ? compare.querySelector('[data-compare-body]') : null;
    if (compare && tbody) (function () {
      var ROWS = {}, KEYS = [];
      each(tbody.querySelectorAll('tr[data-row]'), function (tr) { var k = tr.getAttribute('data-row'); if (!P[k]) return; KEYS.push(k); ROWS[k] = { tr: tr, idx: +tr.getAttribute('data-idx') || 0, sel: null, m: null, r: null }; });
      var sort = { col: '', dir: 'asc' };
      var liveEl = document.createElement('p'); liveEl.className = 'visually-hidden'; liveEl.setAttribute('role', 'status'); liveEl.setAttribute('aria-live', 'polite'); compare.appendChild(liveEl);
      function live(m) { liveEl.textContent = ''; setTimeout(function () { liveEl.textContent = m; }, 60); }
      var costUnit = compare.querySelector('[data-cost-unit]'), caption = compare.querySelector('[data-compare-caption]');
      var hint = compare.querySelector('[data-rate-sort-hint]'), list = compare.querySelector('[data-cost-method-list]');
      var sortSel = compare.querySelector('[data-sort-select]'), sortDir = compare.querySelector('[data-sort-dir]'), sortBar = compare.querySelector('[data-sortbar]');
      var heads = [].slice.call(compare.querySelectorAll('[data-sort]'));
      var COLNAME = {}; heads.forEach(function (b) { var t = b.querySelector('.fss-sortbtn__t'); COLNAME[b.getAttribute('data-sort')] = t ? t.textContent : b.getAttribute('data-sort'); });
      function c() { return C1[shared.cmp]; }

      // "commodity=short condition" lines: the label names the commodity but prints no comparable rate (page-only "See
      // label"; never a finder match, never a cost, never a calculator record)
      function seeFor(p, ck) { var hit = null; String(p.seeNotes || '').split(/\r?\n/).forEach(function (ln) { var i = ln.indexOf('='); if (i > 0 && ln.slice(0, i).trim() === ck) hit = ln.slice(i + 1).trim(); }); return hit; }
      // Row model for the current commodity: kind = ok | see | na | facility | additive | unverified
      function model(k) {
        var p = P[k], cc = c(), e = E();
        if (p.group === 'facility') return { kind: 'facility' };
        if (p.group === 'additive') return cc.basis === 'ton' ? { kind: 'na' } : { kind: 'additive' }; // no labeled mixture for finished feed
        if ((p.direct || []).indexOf(cc.key) < 0) { var sl = seeFor(p, cc.key); return sl ? { kind: 'see', cond: sl } : { kind: 'na' }; }
        if (!e || !e.rateModel) return { kind: 'pending' };
        var m = e.rateModel(p, cc);
        if (m.status === 'ok') { m.kind = 'ok'; return m; }
        if (m.status === 'see') { m.kind = 'see'; return m; }
        if (m.status === 'na') return { kind: 'na' };
        return { kind: 'unverified' };
      }
      function amountText(m, o) { return (E() ? E().rateNum(o.v) : String(o.v)) + ' ' + m.unit; }
      function unitLine(m) { return (m.unit === 'fl oz' && T.productWord ? T.productWord + ' ' : '') + E().unitText(m); }
      function renderRate(k, keepFocus) {
        var R = ROWS[k], m = R.m, p = P[k], td = R.tr.querySelector('[data-rate-for]'), cc = c();
        if (!td || m.kind === 'pending') return; // the estimator has not loaded yet: keep the server text
        if (m.kind === 'ok') {
          var o = m.options[R.sel];
          var seg = td.querySelector('.fss-seg');
          if (keepFocus && seg && seg.children.length === m.options.length) { // update in place so focus stays on the pressed level
            each(seg.querySelectorAll('[data-level]'), function (b) { b.setAttribute('aria-pressed', +b.getAttribute('data-level') === R.sel ? 'true' : 'false'); });
            td.querySelector('[data-rate-amt]').textContent = amountText(m, o);
            var oldNote = td.querySelector('.fss-rate__reset'); if (oldNote && !R.note) oldNote.parentNode.removeChild(oldNote);
            return;
          }
          var h = '<div class="fss-rate"><span class="fss-rate__line"><strong data-rate-amt>' + esc(amountText(m, o)) + '</strong> <span class="fss-rate__u">' + esc(unitLine(m)) + '</span></span>';
          if (m.options.length > 1) {
            h += '<span class="fss-seg" role="group" aria-label="' + esc('Rate level for ' + (p.short || p.title)) + '">' + m.options.map(function (x, i) {
              return '<button type="button" data-level="' + i + '" aria-pressed="' + (i === R.sel ? 'true' : 'false') + '">' + esc(x.t) + '<span class="visually-hidden">: ' + esc(amountText(m, x)) + '</span></button>';
            }).join('') + '</span>';
          } else h += '<span class="fss-rate__tag">' + esc(m.cr === 'derived' ? (T.derived || 'Derived rate') : (T.single || 'Single rate')) + '</span>';
          if (m.cond) h += '<span class="fss-rate__cond">' + esc(m.cond) + '</span>';
          if (R.note) h += '<span class="fss-rate__cond fss-rate__reset">' + esc(R.note) + '</span>';
          td.innerHTML = h + '</div>';
        } else if (m.kind === 'see') {
          td.innerHTML = '<div class="fss-rate"><strong>' + esc(T.seeLabel || 'See label') + '</strong>' + (m.cond ? '<span class="fss-rate__cond">' + esc(m.cond) + '</span>' : '') + '</div>';
        } else if (m.kind === 'facility') {
          td.innerHTML = '<div class="fss-rate"><span class="fss-rate__cond fss-rate__cond--main">' + esc(p.rateText || T.seeLabel || 'See label') + '</span></div>';
        } else if (m.kind === 'additive') {
          td.innerHTML = '<div class="fss-rate"><strong>' + esc(p.rateText || T.dependsMixture || 'Depends on labeled mixture') + '</strong></div>';
        } else if (m.kind === 'na') {
          td.innerHTML = '<span class="fss-rate__na">' + esc(fill(T.notDirect || 'Not for direct treatment of {commodity}', { commodity: lc(cc.label) })) + '</span>';
        } else if (m.kind === 'unverified') {
          td.innerHTML = '<strong>' + esc(T.seeLabel || 'See label') + '</strong>';
        }
      }
      function renderCost(k) {
        var R = ROWS[k], m = R.m, p = P[k], td = R.tr.querySelector('[data-cost-for]'), e = E();
        R.r = null;
        if (!td || m.kind === 'pending') return;
        if (m.kind === 'ok' && e) {
          var r = e.comparison(p, c(), m.options[R.sel].v); R.r = r;
          if (r.status === 'ok') { td.innerHTML = '<span class="fss-cost__v">' + esc(r.text) + '</span>' + (r.fallback ? '<small>' + esc(r.pkg) + ' basis</small>' : ''); return; }
          td.innerHTML = '<span class="fss-cost__na">' + esc(r.status === 'riceForm' ? (T.riceForm || r.short) : r.status === 'noPrice' ? (T.noPrice || r.short) : (r.short || '—')) + '</span><span class="visually-hidden">. ' + esc(r.reason || '') + '</span>';
          return;
        }
        var txt = m.kind === 'additive' ? (T.dependsMixture || 'Depends on labeled mixture') : m.kind === 'facility' ? (T.notApplicable || 'Not applicable') : (m.kind === 'see' || m.kind === 'unverified') ? (T.seeLabel || 'See label') : '';
        if (txt) td.innerHTML = '<span class="fss-cost__na">' + esc(txt) + '</span>';
        else td.innerHTML = '<span aria-hidden="true">—</span><span class="visually-hidden">' + esc(T.notApplicable || 'Not applicable') + '</span>';
      }
      function renderApp(k) { var td = ROWS[k].tr.querySelector('[data-app-for]'); if (td) td.textContent = appFor(P[k], shared.cmp); }
      function renderMethod() {
        if (!list) return;
        var cc = c(), items = ['<li class="fss-method__for">' + esc(cc.label) + ':</li>'];
        if (cc.note) items.push('<li><em>' + esc(cc.note) + '</em></li>');
        orderKeys().forEach(function (k) {
          var R = ROWS[k], p = P[k], nm = '<strong>' + esc(p.short || p.title) + ':</strong> ';
          if (R.r && R.r.status === 'ok') items.push('<li>' + nm + esc(R.r.describe) + ' = <strong>' + esc(R.r.text) + '</strong></li>');
          else if (R.m.kind === 'ok' && R.r) items.push('<li>' + nm + esc(R.r.reason || R.r.short || '') + '.</li>');
          else if (R.m.kind === 'facility') items.push('<li>' + nm + esc(p.rateText || '') + ' ' + esc(T.notApplicable || '') + ' per bushel: a space treatment, not a grain treatment.</li>');
          else if (R.m.kind === 'additive') items.push('<li>' + nm + esc(T.dependsMixture || '') + '; an additive is not a complete treatment, so no cost is ranked.</li>');
          else if (R.m.kind === 'see') items.push('<li>' + nm + esc(T.seeLabel || '') + (R.m.cond ? ' (' + esc(R.m.cond) + ')' : '') + '.</li>');
        });
        list.innerHTML = items.join('');
      }
      function computeAll(resetSel) {
        KEYS.forEach(function (k) {
          var R = ROWS[k]; R.m = model(k);
          if (resetSel || R.sel == null || !R.m.options || R.sel >= R.m.options.length) R.sel = R.m.kind === 'ok' ? R.m.def : null;
          renderApp(k); renderRate(k, false); renderCost(k);
        });
        if (costUnit) costUnit.textContent = c().basis === 'ton' ? (T.costSubTon || '$ per US ton') : (T.costSubBu || '¢ per bushel');
        if (caption) caption.textContent = (T.captionStart ? T.captionStart + ' ' : '') + c().label + (sort.col ? '. Sorted by ' + COLNAME[sort.col] + ', ' + (sort.dir === 'asc' ? T.sortAsc || 'ascending' : T.sortDesc || 'descending').toLowerCase() : '') + '.';
      }

      /* sorting: stable (original order breaks ties); rows without a value stay last in both directions */
      var RATE_GROUPS = ['fl oz|bu', 'lb|bu', 'lb|ton', 'fl oz|ton', 'fl oz|30ton'];
      function sortVal(k, col) {
        var R = ROWS[k], p = P[k];
        switch (col) {
          case 'product': return { s: lc(p.short || p.title) };
          case 'form': return { s: lc(p.formLabel) };
          case 'ai': return { s: lc(p.ai).replace(/[()]/g, '') }; // "(S)-methoprene" sorts as "s-methoprene"
          case 'app': return { s: lc(appFor(p, shared.cmp)) };
          case 'label': return { g: p.labelUrl ? 0 : 1, s: lc(p.short || p.title) };
          case 'rate':
            if (R.m.kind === 'ok') { var gi = RATE_GROUPS.indexOf(R.m.unit + '|' + R.m.basis); return { g: gi < 0 ? RATE_GROUPS.length : gi, n: R.m.options[R.sel].v }; }
            return { last: { see: 1, unverified: 1, facility: 2, additive: 3, na: 4, pending: 5 }[R.m.kind] || 6 };
          case 'cost':
            if (R.r && R.r.status === 'ok') return { n: R.r.value };
            return { last: 1 };
        }
        return {};
      }
      function cmpKeys(a, b) {
        var A = sortVal(a, sort.col), B = sortVal(b, sort.col), d = sort.dir === 'desc' ? -1 : 1;
        if (A.last || B.last) { if (!A.last) return -1; if (!B.last) return 1; if (A.last !== B.last) return A.last - B.last; return ROWS[a].idx - ROWS[b].idx; }
        if ((A.g || 0) !== (B.g || 0)) return (A.g || 0) - (B.g || 0); // unit groups (and label availability) keep their order
        var r = 0;
        if (A.n != null) r = (A.n - B.n) * d; else r = String(A.s).localeCompare(String(B.s), 'en', { sensitivity: 'base', numeric: true }) * d;
        return r || (ROWS[a].idx - ROWS[b].idx);
      }
      function orderKeys() { var ks = KEYS.slice(); if (sort.col) ks.sort(cmpKeys); else ks.sort(function (a, b) { return ROWS[a].idx - ROWS[b].idx; }); return ks; }
      function applySort(announce) {
        var act = document.activeElement, inside = act && tbody.contains(act);
        var want = orderKeys(), have = [].slice.call(tbody.children).map(function (tr) { return tr.getAttribute('data-row'); });
        if (want.join('|') !== have.join('|')) want.forEach(function (k) { tbody.appendChild(ROWS[k].tr); }); // untouched rows keep the browser's scroll anchor
        if (inside && document.activeElement !== act) { try { act.focus({ preventScroll: true }); } catch (e) { act.focus(); } }
        heads.forEach(function (b) {
          var th = b.closest('th'), on = b.getAttribute('data-sort') === sort.col;
          if (on) th.setAttribute('aria-sort', sort.dir === 'asc' ? 'ascending' : 'descending'); else th.removeAttribute('aria-sort');
          b.setAttribute('data-dir', on ? sort.dir : '');
        });
        if (sortSel) sortSel.value = sort.col;
        if (sortDir) { sortDir.disabled = !sort.col; sortDir.setAttribute('data-dir', sort.dir); var dt = sortDir.querySelector('[data-sort-dir-text]'); if (dt) dt.textContent = sort.dir === 'asc' ? (T.sortAsc || 'Ascending') : (T.sortDesc || 'Descending'); }
        if (hint) hint.hidden = sort.col !== 'rate';
        if (caption) caption.textContent = (T.captionStart ? T.captionStart + ' ' : '') + c().label + (sort.col ? '. Sorted by ' + COLNAME[sort.col] + ', ' + (sort.dir === 'asc' ? T.sortAsc || 'ascending' : T.sortDesc || 'descending').toLowerCase() : '') + '.';
        renderMethod();
        if (announce) live(sort.col ? 'Sorted by ' + COLNAME[sort.col] + ', ' + (sort.dir === 'asc' ? (T.sortAsc || 'ascending') : (T.sortDesc || 'descending')).toLowerCase() + '.' : 'Original order.');
      }
      heads.forEach(function (b) {
        b.disabled = false;
        b.addEventListener('click', function () {
          var col = b.getAttribute('data-sort');
          if (sort.col === col) sort.dir = sort.dir === 'asc' ? 'desc' : 'asc'; else { sort.col = col; sort.dir = 'asc'; }
          applySort(true); track('compare_sort', { col: sort.col, dir: sort.dir });
        });
      });
      if (sortBar) sortBar.hidden = false;
      if (sortSel) { sortSel.disabled = false; sortSel.addEventListener('change', function () { sort.col = sortSel.value; if (!sort.col) sort.dir = 'asc'; applySort(true); track('compare_sort', { col: sort.col || 'original', dir: sort.dir }); }); }
      if (sortDir) sortDir.addEventListener('click', function () { if (!sort.col) return; sort.dir = sort.dir === 'asc' ? 'desc' : 'asc'; applySort(true); });
      // When the table is stacked into labelled blocks (narrow section, or enlarged text: a container query in rem), the
      // header row is visually hidden and its buttons leave the tab order (the "Sort by" bar replaces them).
      var ctable = tbody.closest('table'), mq = window.matchMedia ? window.matchMedia('(max-width: 989px)') : null;
      function isStacked() { return ctable ? getComputedStyle(ctable).display !== 'table' : !!(mq && mq.matches); }
      function headTab() { var s = isStacked(); heads.forEach(function (b) { if (s) b.setAttribute('tabindex', '-1'); else b.removeAttribute('tabindex'); }); }
      headTab();
      if (window.ResizeObserver) new ResizeObserver(headTab).observe(compare);
      else if (mq) { if (mq.addEventListener) mq.addEventListener('change', headTab); else if (mq.addListener) mq.addListener(headTab); }

      // rate level buttons (delegated): recompute that row's cost at once; re-sort if the order depends on it
      tbody.addEventListener('click', function (ev) {
        var b = ev.target.closest('[data-level]'); if (!b) return;
        var tr = b.closest('tr[data-row]'), k = tr && tr.getAttribute('data-row'), R = k && ROWS[k];
        if (!R || R.m.kind !== 'ok') return;
        var lvl = +b.getAttribute('data-level'); if (lvl === R.sel) return;
        R.sel = lvl; R.want = R.m.options[lvl].t; R.note = ''; renderRate(k, true); renderCost(k);
        if (sort.col === 'rate' || sort.col === 'cost') applySort(false); else renderMethod();
        var p = P[k], o = R.m.options[lvl];
        live((p.short || p.title) + ': ' + o.t + ', ' + amountText(R.m, o) + (R.r && R.r.status === 'ok' ? '. Estimated cost ' + R.r.text : '') + '.');
        track('compare_rate_level', { product: p.handle, level: o.t });
      });

      function show(key, user) {
        KEYS.forEach(function (k) { ROWS[k].note = ''; });
        computeAll(true);            // every row starts from its own valid default for the new commodity ...
        var reset = [];              // ... then a level the visitor chose is kept wherever the new commodity lists the same level
        KEYS.forEach(function (k) {
          var R = ROWS[k]; if (!R.want || R.m.kind !== 'ok') return; // no choice made, or no rate here (the choice is kept for later)
          var i = -1; R.m.options.forEach(function (o, j) { if (o.t === R.want) i = j; });
          if (i === R.sel) return;
          if (i >= 0) R.sel = i;
          else { R.note = fill(T.rateReset || '{level} is not listed for {commodity}. Showing the label default.', { level: R.want, commodity: lc(C1[key].label) }); reset.push(P[k].short || P[k].title); }
          renderRate(k, false); renderCost(k);
        });
        applySort(false);
        if (user) live('Showing ' + C1[key].label + ' rates and estimated costs.' + (reset.length ? ' Rate level reset to the label default for ' + reset.join(', ') + '.' : ''));
      }
      hooks.compare = show;
      computeAll(true); applySort(false);
      onReady(function () { computeAll(false); applySort(false); }); // the estimator is deferred; rates and costs fill in once it has loaded
    })();

    /* ---------------- Help me choose: commodity strip + goal cards, either order ---------------- */
    var disc = root.querySelector('[data-finder-disclosure]');
    if (!disc) { paintStrips(); return; }
    var fbody = disc.querySelector('[data-finder-body]'); if (fbody) fbody.hidden = false;
    var goalWrap = disc.querySelector('[data-goalcards]');
    if (!goalWrap) { paintStrips(); return; }
    var resetBtn = disc.querySelector('[data-finder-reset]');
    var box = disc.querySelector('[data-results]'), body = disc.querySelector('[data-results-body]'), rtitle = disc.querySelector('[data-results-title]');
    var status = disc.querySelector('[data-status]');
    var goal = null;
    function announce(msg) { if (!status) return; status.textContent = ''; setTimeout(function () { status.textContent = msg; }, 60); }
    function setCur() {
      root.setAttribute('data-fss-cur1', shared.fc1 ? (C1[shared.fc1].formText || C1[shared.fc1].label) : '');
      root.setAttribute('data-fss-cur2', goal ? C2[goal].label : '');
    }
    function paintGoals() { each(goalWrap.querySelectorAll('[data-goal]'), function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-goal') === goal ? 'true' : 'false'); }); }
    function clearResults() { box.hidden = true; body.innerHTML = ''; rtitle.textContent = ''; }
    function prompt(msg, title) { rtitle.textContent = title || ''; rtitle.hidden = !title; body.innerHTML = '<p class="fss-note fss-note--prompt">' + esc(msg) + '</p>'; box.hidden = false; }
    function update(speak) {
      setCur(); paintGoals();
      if (resetBtn) resetBtn.hidden = !shared.fc1 && !goal;
      var g = goal ? C2[goal] : null, cc = shared.fc1 ? C1[shared.fc1] : null;
      if (!g) {
        if (cc) { prompt(T.needGoal || 'Now choose a goal.', ''); if (speak) announce(cc.label + ' selected. ' + (T.needGoal || '')); } else { clearResults(); }
        return;
      }
      if (g.kind === 'direct' && !cc) { prompt(T.needCommodity || 'Choose a commodity.', g.label); if (speak) announce(g.label + ' selected. ' + (T.needCommodity || '')); return; }
      rtitle.hidden = false;
      render(speak);
      box.hidden = false;
      track('finder_step', { goal: g.key, commodity: cc ? cc.key : '' });
    }
    hooks.finder = function (user) { update(user); };
    goalWrap.addEventListener('click', function (ev) {
      var b = ev.target.closest('[data-goal]'); if (!b) return;
      goal = b.getAttribute('data-goal'); update(true); // a goal change keeps the commodity
    });
    if (resetBtn) resetBtn.addEventListener('click', function () {
      shared.fc1 = null; goal = null; paintStrips(); clearResults(); setCur(); paintGoals(); resetBtn.hidden = true;
      var first = disc.querySelector('[data-cstrip] [data-commodity]'); if (first) first.focus(); announce('Choices cleared.'); track('finder_reset', {});
    });
    paintStrips(); setCur();

    function reasonFor(p, gk) {
      var lines = String(p.reasons || '').split(/\n/);
      for (var i = 0; i < lines.length; i++) { var m = lines[i].split('='); if (m.length > 1 && m[0].trim() === gk) return m.slice(1).join('=').trim(); }
      return p.reason || '';
    }
    function candidates(g) { return uniq((g.lead || []).concat(g.more || [])).filter(function (k) { return P[k]; }); }
    function chipFacility() { return '<span class="fss-chip fss-chip--facility">' + esc(T.chipFacility || 'Facility treatment') + '</span>'; }
    function askLink(cc, g, productTitle, label, req) {
      return '<a class="fss-textlink" href="#' + esc(cfg.helpAnchor) + '" data-fss-request="' + (req || 'product') + '" data-fss-commodity-text="' + esc(cc ? (cc.formText || cc.label) : '') + '" data-fss-goal-text="' + esc(g.label) + '"' + (productTitle ? ' data-fss-product-title="' + esc(productTitle) + '"' : '') + ' data-fss-track="finder_help">' + esc(label) + '</a>';
    }
    function render(speak) {
      var g = C2[goal], cc = shared.fc1 ? C1[shared.fc1] : null, list = [], notes = [];
      var ctx = { commodity: cc ? cc.label : '', goal: g.label }, ctxL = { commodity: cc ? lc(cc.label) : '', goal: lc(g.label) };
      rtitle.textContent = g.resultsHeading ? fill(g.resultsHeading, ctx) : (g.label + (cc ? ' · ' + (cc.formText || cc.label) : ''));
      if (g.kind === 'facility') {
        list = candidates(g).filter(function (k) { return (P[k].facility || []).indexOf(g.facilityUse) >= 0; });
        notes.push('<p>' + chipFacility() + ' ' + esc(cc ? fill(T.facilityNote, ctxL) : (T.facilityNoteAny || '')) + '</p>');
        if (g.note) notes.push('<p>' + esc(fill(g.note, ctx)) + '</p>');
      } else {
        var restrict = (cc.restrict || []).filter(Boolean);
        list = candidates(g).filter(function (k) {
          var p = P[k];
          if (p.group !== 'chemical') return false;
          if ((p.direct || []).indexOf(cc.key) < 0) return false;
          if (restrict.length && restrict.indexOf(k) < 0) return false;
          if (g.form && g.form !== 'any' && p.form !== g.form) return false;
          return true;
        });
        if (restrict.length && cc.restrictNote && list.length) notes.push('<p>' + esc(fill(cc.restrictNote, ctx)) + '</p>');
        if (cc.note) notes.push('<p>' + esc(fill(cc.note, ctx)) + '</p>');
        if (g.note && list.length) notes.push('<p>' + esc(fill(g.note, ctx)) + '</p>');
      }
      var html = notes.length ? '<div class="fss-note' + (g.kind === 'facility' ? ' fss-note--facility' : '') + '">' + notes.join('') + '</div>' : '';
      if (!list.length) {
        var nng = (cc && cc.noneNoteGoals || []).filter(Boolean);
        var why = (g.kind === 'direct' && cc && (cc.restrict || []).filter(Boolean).length && cc.noneNote && (!nng.length || nng.indexOf(g.key) >= 0)) ? '<p>' + esc(fill(cc.noneNote, ctx)) + '</p>' : '';
        html += '<div class="fss-note"><p>' + esc(fill(g.none || T.noneMessage, ctxL)) + '</p>' + why + '<p>' + askLink(cc, g, '', T.askLabel || 'Ask FSS') + '</p></div>';
        body.innerHTML = html;
        if (speak) announce(rtitle.textContent + ': no verified product match. ' + (T.askLabel || 'Ask FSS') + ' is available.');
        track('finder_results', { first: cc ? cc.key : '', second: g.key, results: 0 });
        return;
      }
      // Leading products first: the eligible "lead" products (up to maxLead); if none is eligible, the first eligible
      // others. Everything else sits behind "See all matches".
      var max = cfg.maxLead || 3, leads = g.lead || [];
      var nLead = list.filter(function (k) { return leads.indexOf(k) >= 0; }).length;
      var shown = Math.min(max, nLead || list.length);
      html += '<ul class="fss-mcards" role="list">' + list.map(function (k, i) { return card(P[k], k, cc, g, i >= shown); }).join('') + '</ul>';
      if (list.length > shown) html += '<p class="fss-more"><button type="button" class="fss-btn fss-btn--secondary fss-btn--small" data-see-all aria-expanded="false">' + esc(T.seeAll || 'See all matches') + ' (' + list.length + ')</button></p>';
      body.innerHTML = html;
      var btn = body.querySelector('[data-see-all]');
      if (btn) btn.addEventListener('click', function () {
        var hid = body.querySelectorAll('.fss-mc[hidden]');
        each(hid, function (el) { el.hidden = false; });
        btn.setAttribute('aria-expanded', 'true'); btn.hidden = true;
        if (hid[0]) focusQuietly(hid[0].querySelector('.fss-mc__title a'));
        announce(hid.length + ' more shown.');
      });
      if (speak) announce(rtitle.textContent + ': ' + list.length + ' match' + (list.length === 1 ? '' : 'es') + '.');
      track('finder_results', { first: cc ? cc.key : '', second: g.key, results: list.length });
    }
    function card(p, k, cc, g, extra) {
      var e = E();
      var price = p.hidePrice ? '' : ((p.priceVaries ? (T.fromLabel || 'From') + ' ' : '') + money(p.priceMin));
      var reason = reasonFor(p, g.key);
      var chip = g.kind === 'facility' ? chipFacility() : '';
      var estHtml = '';
      if (g.kind === 'direct' && cc && e) {
        // the same default level as the comparison row, so both show the same estimate
        var m = e.rateModel ? e.rateModel(p, cc) : null, o = m && m.status === 'ok' ? m.options[m.def] : null;
        var r = o ? e.comparison(p, cc, o.v) : e.comparison(p, cc);
        if (r.status === 'ok') estHtml = '<span class="fss-mc__est">' + esc(T.estPrefix || 'Est. chemical cost') + ' ' + esc(r.text) + (o && m.mode !== 'single' ? ' <small>(' + esc(o.t.toLowerCase()) + ' rate)</small>' : (m && m.cr === 'derived' ? ' <small>(' + esc(lc(T.derived || 'Derived rate')) + ')</small>' : '')) + (r.fallback ? ' <small>(' + esc(r.pkg) + ' basis)</small>' : '') + '</span>';
      }
      var cp = (g.kind === 'direct' && cc && e && cfg.calcUrl) ? e.calcParams(p, cc) : null;
      // the calculator keeps its own record: say so when it will open at a different rate than this estimate
      var calcNote = (cp && o && cp.rate > 0 && Math.abs(cp.rate - o.v) > 1e-6 * Math.max(1, cp.rate)) ? '<p class="fss-mc__note">' + esc(fill(T.calcRateNote || 'The calculator uses the label’s {rate} rate.', { rate: e.rateNum(cp.rate) + ' ' + m.unit + ' ' + e.unitText(m) })) + '</p>' : '';
      var sep = ' <span aria-hidden="true">·</span> ';
      var fit = String(p.fit || '').split('|'), fitStyle = fit.length === 3 ? ' style="--k:' + esc(fit[0]) + ';--tx:' + esc(fit[1]) + '%;--ty:' + esc(fit[2]) + '%"' : '';
      var acts = ['<a class="fss-textlink" href="' + esc(p.url) + '">' + esc(T.viewLabel || 'View product') + '<span class="visually-hidden">: ' + esc(p.short || p.title) + '</span></a>'];
      if (cp) acts.push('<a class="fss-textlink" href="' + esc(e.calcHref(cfg.calcUrl, cp)) + '" data-fss-track="calculator_click" data-fss-product="' + esc(p.handle) + '">' + esc(T.calcLabel || 'Calculate quantity') + '<span class="visually-hidden"> for ' + esc(p.short || p.title) + '</span></a>');
      if (g.kind !== 'direct') acts.push(askLink(cc, g, p.title, T.askLabel || 'Ask FSS', 'product'));
      return '<li class="fss-mc" data-key="' + esc(k) + '"' + (extra ? ' hidden' : '') + '>' +
        '<a class="fss-mc__media" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' + (p.image ? '<img src="' + esc(p.image) + '" alt="" width="120" height="120" loading="lazy"' + fitStyle + '>' : '') + '</a>' +
        '<div class="fss-mc__body">' + chip +
        '<h4 class="fss-mc__title"><a href="' + esc(p.url) + '">' + esc(p.short || p.title) + '</a></h4>' +
        (p.role ? '<p class="fss-mc__role">' + esc(p.role) + '</p>' : '') +
        (reason ? '<p class="fss-mc__why">' + esc(reason) + '</p>' : '') +
        (p.note ? '<p class="fss-mc__note">' + esc(p.note) + '</p>' : '') +
        ((price || estHtml) ? '<p class="fss-mc__meta">' + (price ? '<span class="fss-mc__price">' + esc(price) + '</span>' : '') + (price && estHtml ? sep : '') + estHtml + '</p>' : '') + calcNote +
        '<p class="fss-mc__actions">' + acts.join(' ') + '</p>' +
        '</div></li>';
    }
  });
})();
